import React, { useEffect, useState } from 'react';
import { collection, query, orderBy, getDocs, deleteDoc, doc } from 'firebase/firestore';
import { db } from '../firebase';
import { Download, Play, Trash2, Search, FileText } from 'lucide-react';
import jsPDF from 'jspdf';

const History = () => {
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');

  const fetchHistory = async () => {
    setLoading(true);
    try {
      const q = query(collection(db, 'translations'), orderBy('timestamp', 'desc'));
      const snapshot = await getDocs(q);
      const docs = [];
      snapshot.forEach((doc) => docs.push({ id: doc.id, ...doc.data() }));
      setHistory(docs);
    } catch (error) {
      console.error("Error fetching history: ", error);
    }
    setLoading(false);
  };

  useEffect(() => {
    fetchHistory();
  }, []);

  const handleDelete = async (id) => {
    try {
      await deleteDoc(doc(db, 'translations', id));
      setHistory(history.filter(h => h.id !== id));
    } catch (e) {
      console.error(e);
    }
  };

  const speak = (text) => {
    const u = new SpeechSynthesisUtterance(text);
    speechSynthesis.cancel();
    speechSynthesis.speak(u);
  };

  const exportPDF = () => {
    const doc = new jsPDF();
    doc.setFontSize(22);
    doc.text("SignAI Translation Transcript", 20, 20);
    doc.setFontSize(12);
    doc.text(`Generated on: ${new Date().toLocaleString()}`, 20, 30);
    
    let y = 50;
    doc.setFontSize(14);
    
    const filtered = history.filter(h => h.text.toLowerCase().includes(searchTerm.toLowerCase()));
    
    filtered.forEach((item, i) => {
      if (y > 270) { doc.addPage(); y = 20; }
      const time = item.timestamp ? new Date(item.timestamp.seconds * 1000).toLocaleString() : 'Unknown Time';
      doc.setFontSize(10);
      doc.setTextColor(150);
      doc.text(time, 20, y);
      doc.setFontSize(12);
      doc.setTextColor(0);
      doc.text(`"${item.text}"`, 20, y + 6);
      y += 18;
    });

    doc.save("SignAI_Transcript.pdf");
  };

  const filteredHistory = history.filter(h => h.text.toLowerCase().includes(searchTerm.toLowerCase()));

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
      <div className="glass-panel" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '16px' }}>
        <div>
          <h2 style={{ margin: 0, color: 'var(--text-main)', display: 'flex', alignItems: 'center', gap: '12px' }}>
            <FileText size={28} color="var(--primary-color)" /> Translation Logs
          </h2>
          <p style={{ margin: '4px 0 0', color: 'var(--text-muted)' }}>Securely stored cloud transcripts of your sessions.</p>
        </div>
        
        <button className="btn btn-primary" onClick={exportPDF} disabled={filteredHistory.length === 0}>
          <Download size={18} /> Export PDF
        </button>
      </div>

      <div className="glass-panel" style={{ minHeight: '60vh' }}>
        <div style={{ display: 'flex', gap: '16px', marginBottom: '24px' }}>
          <div style={{ position: 'relative', flex: 1 }}>
            <Search size={18} style={{ position: 'absolute', left: '16px', top: '50%', transform: 'translateY(-50%)', color: 'var(--text-muted)' }} />
            <input 
              type="text" 
              placeholder="Search past translations..." 
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              style={{ 
                width: '100%', padding: '12px 16px 12px 42px', 
                borderRadius: '12px', border: '1px solid var(--glass-border)',
                background: 'rgba(0,0,0,0.03)', color: 'var(--text-main)',
                fontFamily: 'inherit', fontSize: '15px'
              }}
            />
          </div>
        </div>

        {loading ? (
          <div style={{ textAlign: 'center', padding: '40px', color: 'var(--text-muted)' }}>Loading history from cloud...</div>
        ) : filteredHistory.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '60px', color: 'var(--text-muted)', background: 'rgba(0,0,0,0.02)', borderRadius: '12px' }}>
            No translations found. Start signing and click "Save to Cloud".
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
            {filteredHistory.map((item) => {
              const time = item.timestamp ? new Date(item.timestamp.seconds * 1000).toLocaleString() : 'Just now';
              return (
                <div key={item.id} style={{ 
                  display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '12px',
                  padding: '16px 20px', borderRadius: '12px',
                  background: 'var(--card-bg)', border: '1px solid var(--glass-border)',
                  boxShadow: '0 2px 8px rgba(0,0,0,0.02)'
                }}>
                  <div>
                    <h3 style={{ margin: '0 0 4px', fontSize: '16px', color: 'var(--text-main)' }}>"{item.text}"</h3>
                    <span style={{ fontSize: '12px', color: 'var(--text-muted)' }}>{time} • {item.signsCount || item.text.split(' ').length} signs</span>
                  </div>
                  <div style={{ display: 'flex', gap: '8px' }}>
                    <button className="btn" onClick={() => speak(item.text)} style={{ padding: '8px', background: 'rgba(101,146,135,0.1)', color: 'var(--primary-color)' }}>
                      <Play size={16} />
                    </button>
                    <button className="btn" onClick={() => handleDelete(item.id)} style={{ padding: '8px', background: 'rgba(239,68,68,0.1)', color: 'var(--danger)' }}>
                      <Trash2 size={16} />
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default History;
